import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { closeCenteredModal } from "../../../REDUX/actions/centeredModal";
import { deleteIaDocument } from "../../../REDUX/actions/iaAdmin";
import DeleteBtn from "../../Common/FormElements/DeleteBtn";
import Button from "../../Common/FormElements/Button/Button";

/**
 * Confirmation de retrait d'un document du corpus.
 *
 * Le ConfirmModal generique du projet n'affiche qu'une ligne ; ici la
 * consequence n'est pas la meme selon l'origine du document, et l'un des deux
 * cas est irreversible. Elle est donc ecrite explicitement avant l'action.
 *
 * Les deux branches suivent purgeOne() dans utils/iaIngest.js :
 * - depot manuel        -> retire d'OpenAI, fichier supprime de la plateforme,
 *                          ligne supprimee. Definitif.
 * - guide ou produit    -> retire d'OpenAI et marque `excluded`. Le PDF reste
 *                          disponible dans la FAQ ou la fiche produit.
 */
const IaDeleteDocumentModal = ({ document }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const [loading, setLoading] = useState(false);

  // Meme condition que purgeOne, a partir du flag renvoye par l'API plutot que
  // devinee ici : le texte annonce donc exactement ce que fera le serveur.
  const isManual = document.sourceType === "manual" && !document.hasAttachment;

  const handleDelete = async () => {
    try {
      setLoading(true);
      const done = await dispatch(deleteIaDocument(document.id, t));
      if (done) dispatch(closeCenteredModal());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-3">
      <p className="f16 sBold iaBlue mb-0">
        {t("Remove this document from the assistant?")}
      </p>

      <p className="f14 normal text-lightBlack mb-0">
        {document.title || document.fileName}
      </p>

      <div className="iaWarning br5 p-3">
        <p className="f13 sBold mb-2">{t("What will happen")}</p>
        <ul className="f13 normal mb-0 ps-3">
          <li>
            {t(
              "The file is removed from OpenAI: the assistant can no longer use its content to answer."
            )}
          </li>
          {isManual ? (
            <>
              <li>
                {t(
                  "This document was uploaded manually: the file is also deleted from the platform."
                )}
              </li>
              <li className="sBold">{t("This action cannot be undone.")}</li>
            </>
          ) : (
            <>
              <li>
                {t(
                  "The PDF remains available in the FAQ or on the product sheet: only its use by the assistant is removed."
                )}
              </li>
              <li>
                {t(
                  "The document is marked as excluded, so the nightly synchronisation will not put it back into the corpus."
                )}
              </li>
            </>
          )}
        </ul>
      </div>

      <div className="d-flex gap-3 justify-content-center">
        <DeleteBtn text="Delete" loading={loading} onClick={handleDelete} />
        <Button onClick={() => dispatch(closeCenteredModal())}>{t("Cancel")}</Button>
      </div>
    </div>
  );
};

export default IaDeleteDocumentModal;
